import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Instructions modal fired by the BigCommerce tile on Step 3's card
 * grid. Per ADR 0011 we can't Mailchute-initiate the BC install — BC
 * always initiates from the merchant's store admin. The modal
 * surfaces the same step-by-step the wizard's body used to carry,
 * packaged to fit the card-grid affordance ("click Connect → modal
 * opens").
 *
 * The merchant follows the instructions inside BC; the existing
 * `/bc/oauth/callback` route handles the post-install redirect.
 * There is no Mailchute-side OAuth handler invoked from this modal.
 */
interface BigCommerceConnectDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function BigCommerceConnectDialog({ open, onClose }: BigCommerceConnectDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect your BigCommerce store</DialogTitle>
          <DialogDescription>
            BigCommerce starts the install from inside your store admin — Mailchute can&apos;t kick
            it off remotely.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ol className="text-muted-foreground list-decimal space-y-2 pl-5 text-sm">
            <li>Open your BigCommerce store&apos;s admin panel.</li>
            <li>
              Go to <span className="text-foreground font-medium">Apps</span> → search for Mailchute
              in the marketplace.
            </li>
            <li>
              Click <span className="text-foreground font-medium">Install</span> and approve the
              requested permissions.
            </li>
            <li>
              You&apos;ll be brought back here automatically, and your store will be linked without
              any extra steps.
            </li>
          </ol>
          <p>
            <a
              href="https://login.bigcommerce.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
            >
              Open BigCommerce login
              <ExternalLink className="size-3.5" aria-hidden="true" />
            </a>
          </p>
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
